import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Briefcase } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

interface ClientService {
    id: string;
    name: string;
    description: string;
    price: number;
    status: "active" | "completed" | "paused";
}

export const ClientServices = ({ client }: { client: any }) => {
    const { updateClient } = useDespachos();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Obtener los datos del address serializado
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }

    const servicesList: ClientService[] = extraData.services || [];

    const handleCreateService = async () => {
        if (!name.trim()) return;

        const newService: ClientService = {
            id: Math.random().toString(36).substring(2, 9),
            name: name.trim(),
            description: description.trim(),
            price: parseFloat(price) || 0,
            status: "active"
        };

        const updatedExtra = {
            ...extraData,
            services: [...servicesList, newService]
        };

        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        // Limpiar
        setName("");
        setDescription("");
        setPrice("");
        setIsOpen(false);
    };

    const handleDeleteService = async (serviceId: string) => {
        const updatedServices = servicesList.filter(s => s.id !== serviceId);
        const updatedExtra = {
            ...extraData,
            services: updatedServices
        };

        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });
    };

    // Formateador
    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0
        }).format(val);
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Servicios Contratados</CardTitle>
                    <p className="text-xs text-gray-400">Servicios legales y planes de vigilancia activos para este cliente</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Registrar Servicio
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Registrar Nuevo Servicio</DialogTitle>
                            <p className="text-xs text-gray-400">Vincula un servicio prestado o contratado</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Nombre del Servicio *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Vigilancia Judicial Premium" value={name} onChange={e => setName(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Descripción</Label>
                                <Input className="rounded-xl" placeholder="Ej: Reporte semanal de radicados..." value={description} onChange={e => setDescription(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Tarifa / Precio</Label>
                                <Input className="rounded-xl" placeholder="$0.00" value={price} onChange={e => setPrice(e.target.value)} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreateService} disabled={!name.trim()}>Registrar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {servicesList.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Servicio</TableHead>
                                <TableHead className="font-semibold text-gray-500">Descripción</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Precio</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {servicesList.map((s) => (
                                <TableRow key={s.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">{s.name}</TableCell>
                                    <TableCell className="text-gray-500 text-xs max-w-[200px] truncate">{s.description || '—'}</TableCell>
                                    <TableCell>
                                        <span className="text-[10px] font-bold py-0.5 px-2 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                                            Activo
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right font-bold text-gray-900 text-xs">
                                        {formatCurrency(s.price)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteService(s.id)}
                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Briefcase className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay servicios contratados o registrados.</p>
                        <p className="text-xs text-gray-300 mt-1">Registra los servicios prestados usando el botón superior.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
